// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import {
  AssetSearchListing,
  assetSearchListingSchema,
} from '@/asset/hooks/use-asset-filters';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { useSort } from '@/hooks/use-sort';
import { LocationErrorBoundary } from '@/location/components/location-error-boundary';
import { ZonesTabContent } from '@/location/components/zones-tab-content';
import { useLocation } from '@/location/hooks/use-location';
import { Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

// Columns to display in the asset table
const ASSET_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'product',
  'owner',
  'activity',
];

export const Route = createFileRoute('/_app/locations/$locationKey/')({
  component: LocationIndexRoute,
  validateSearch: assetSearchListingSchema,
});

function LocationIndexRoute() {
  const { locationKey } = Route.useParams();
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleChangePage = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  return (
    <LocationErrorBoundary>
      <LocationIndexContent
        locationKey={locationKey}
        assetSearchParams={searchParameters}
        handlePageChange={handleChangePage}
      />
    </LocationErrorBoundary>
  );
}

interface LocationIndexContentProps {
  locationKey: string;
  assetSearchParams?: AssetSearchListing;
  handlePageChange?: (page: number) => void;
}

function LocationIndexContent(props: LocationIndexContentProps) {
  const { location } = useLocation(props.locationKey);

  if (location.type === 'SITE') {
    return (
      <ZonesTabContent location={location} locationKey={props.locationKey} />
    );
  }

  return (
    <RelatedAssetsContent
      locationId={location.id}
      assetSearchParams={props.assetSearchParams}
      handlePageChange={props.handlePageChange}
    />
  );
}

interface RelatedAssetsContentProps {
  locationId: string;
  assetSearchParams?: AssetSearchListing;
  handlePageChange?: (page: number) => void;
}

function RelatedAssetsContent(props: RelatedAssetsContentProps) {
  const { sort, setSort } = useSort<AssetSortProperty>('serial');

  return (
    <HardfinTabCardWrapper title="Related assets">
      <Stack paddingTop={2}>
        <Suspense
          fallback={<AssetTableSkeleton columns={ASSET_TABLE_COLUMNS.length} />}
        >
          <AssetTable
            sort={sort}
            columns={ASSET_TABLE_COLUMNS}
            filter={{
              locationIds: [props.locationId],
              pageSize: 50,
              pageNumber: props.assetSearchParams?.page ?? 1,
            }}
            onSort={setSort}
            onPageChange={props.handlePageChange}
            emptyState={
              <Typography variant="body2" color="text.secondary">
                There are no assets at this location
              </Typography>
            }
          />
        </Suspense>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
